/** Who is signed in, and what the market is doing. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import {
  isSessionOpen,
  sessionSecondsBetween,
  sessionSecondsRemaining,
  sessionState,
} from '@metal/shared'
import { ApiError, api, onSessionExpired, request, tokens } from './api'

export type MembershipStatus =
  | 'basic'
  | 'limited'
  | 'pending'
  | 'active'
  | 'suspended'
  | 'rejected'

export interface SessionUser {
  partyId: string
  partyNumber: number
  displayName: string
  shortName: string
  /** The avatar's letter, as the party or the staff member chose it. */
  avatarLetter: string | null
  /** A staff member's job title. Null for a member. */
  titleFa: string | null
  mobile: string
  partyKind: 'person' | 'company'
  membership: MembershipStatus
  sides: Array<'buy' | 'sell'>
  grade: 'gold' | 'silver' | 'bronze' | 'none' | null
  gradeScore: number | null
  province: string | null
  roles: string[]
  permissions: string[]
  operator: boolean
  /** Server step of the onboarding file, when it is unfinished. */
  onboardingStep: number | null
  unreadNotifications: number
}

/** `GET /auth/session`: who is signed in and what they were granted. */
interface SessionView {
  user: {
    userNo: number
    displayName: string
    platformRole: string | null
    titleFa?: string | null
    avatarLetter?: string | null
  }
  party: { partyNo: number; kind: 'PERSON' | 'COMPANY'; status: string; tradingRole: string | null }
  companyPermissions: string[]
  platformDomains: Record<string, string> | null
  /** The keys the server's own guards read, expanded by the server. */
  permissions?: string[]
}

/** `GET /me`: the party's own file, which is where the card and the province live. */
interface MeView {
  user: { fullName: string; mobile: string } | null
  party: {
    partyId: string
    partyNo: number
    kind: 'PERSON' | 'COMPANY'
    displayName: string
    shortName?: string
    avatarLetter?: string | null
    membership: MembershipStatus
    province: string | null
    tradingRole: 'BUYER' | 'SELLER' | 'BOTH' | null
    grade: 'GOLD' | 'SILVER' | 'BRONZE' | 'NONE' | null
    gradeScore: number | null
  }
  kycCase: { step: number } | null
}

function principalOf(
  session: SessionView,
  me: MeView | null,
  unread: number,
): SessionUser {
  const operator = session.user.platformRole !== null
  const tradingRole = me?.party.tradingRole ?? session.party.tradingRole
  const sides: Array<'buy' | 'sell'> =
    tradingRole === 'BOTH'
      ? ['buy', 'sell']
      : tradingRole === 'SELLER'
        ? ['sell']
        : tradingRole === 'BUYER'
          ? ['buy']
          : []
  const membership = me?.party.membership ?? 'basic'
  const name = me?.party.displayName ?? session.user.displayName
  const grade = me?.party.grade ?? null

  return {
    partyId: me?.party.partyId ?? '',
    partyNumber: me?.party.partyNo ?? session.party.partyNo,
    displayName: name,
    shortName: me?.party.shortName ?? name,
    // Staff sign in under the platform's own party, so their letter is their own.
    avatarLetter: (operator ? session.user.avatarLetter : me?.party.avatarLetter) ?? null,
    titleFa: session.user.titleFa ?? null,
    mobile: me?.user?.mobile ?? '',
    partyKind: (me?.party.kind ?? session.party.kind) === 'PERSON' ? 'person' : 'company',
    membership,
    sides,
    grade: grade === null ? null : (grade.toLowerCase() as SessionUser['grade']),
    gradeScore: me?.party.gradeScore ?? null,
    province: me?.party.province ?? null,
    roles: session.user.platformRole ? [session.user.platformRole] : [],
    permissions:
      session.permissions ??
      (operator
        ? Object.entries(session.platformDomains ?? {})
            .filter(([, level]) => level !== 'none')
            .flatMap(([domain, level]) => [domain, `${domain}:${level}`])
        : session.companyPermissions),
    operator,
    // Only an unfinished file has a step to go back to.
    onboardingStep: membership === 'active' ? null : (me?.kycCase?.step ?? null),
    unreadNotifications: unread,
  }
}

interface SessionContextValue {
  user: SessionUser | null
  loading: boolean
  /** Re-reads the principal, after activation or a role change. */
  reload: () => Promise<void>
  signOut: () => Promise<void>
  setAccessToken: (token: string) => Promise<void>
  can: (permission: string) => boolean
  isSeller: boolean
  isBuyer: boolean
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({
  children,
  scope = 'member',
  onIdentityChange,
}: {
  children: ReactNode
  /** Called whenever the person behind the session may change: */
  onIdentityChange?: () => void
  /** The operator console reads the session alone: */
  scope?: 'member' | 'operator'
}) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)
  const lastParty = useRef<string | null>(null)
  const reset = useRef(onIdentityChange)
  reset.current = onIdentityChange

  const load = useCallback(async () => {
    try {
      const session = await api.get<SessionView>('/auth/session')
      if (scope === 'operator') {
        const next = principalOf(session, null, 0)
        if (lastParty.current && lastParty.current !== next.partyId) reset.current?.()
        lastParty.current = next.partyId
        setUser(next)
        return
      }
      const [me, summary] = await Promise.all([
        api.get<MeView>('/me').catch(() => null),
        api.get<{ unread: number }>('/notifications/summary').catch(() => null),
      ])
      const next = principalOf(session, me, summary?.unread ?? 0)
      if (lastParty.current && lastParty.current !== next.partyId) reset.current?.()
      lastParty.current = next.partyId
      setUser(next)
    } catch (e) {
      // Only an answer signs somebody out.
      if (!(e instanceof ApiError && e.status === 0)) setUser(null)
    } finally {
      setLoading(false)
    }
  }, [scope])

  useEffect(() => {
    void load()
  }, [load])

  // A lapsed session is a signed out one:
  useEffect(
    () =>
      onSessionExpired(() => {
        lastParty.current = null
        reset.current?.()
        setUser(null)
      }),
    [],
  )

  const signOut = useCallback(async () => {
    await api.post('/auth/session/logout').catch(() => undefined)
    tokens.set(null)
    lastParty.current = null
    reset.current?.()
    setUser(null)
  }, [])

  const setAccessToken = useCallback(
    async (token: string) => {
      tokens.set(token)
      // A fresh sign in starts from nothing cached.
      lastParty.current = null
      reset.current?.()
      setLoading(true)
      await load()
    },
    [load],
  )

  const value = useMemo<SessionContextValue>(
    () => ({
      user,
      loading,
      reload: load,
      signOut,
      setAccessToken,
      can: (permission: string) => Boolean(user?.permissions?.includes(permission)),
      isSeller: Boolean(user?.sides?.includes('sell')),
      isBuyer: Boolean(user?.sides?.includes('buy')),
    }),
    [user, loading, load, signOut, setAccessToken],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside SessionProvider')
  return ctx
}

export interface MarketClock {
  /** The server's idea of now, corrected for client skew. */
  now: Date
  open: boolean
  closesAt: Date | null
  opensAt: Date | null
  /** Seconds of session time left until `expiresAt`. */
  remaining: (expiresAt: Date | string) => number
  /** Session seconds burned since `from`. */
  elapsedSince: (from: Date | string) => number
  frozen: boolean
}

const MarketClockContext = createContext<MarketClock | null>(null)

export function MarketClockProvider({ children }: { children: ReactNode }) {
  const skew = useRef(0)
  const [tick, setTick] = useState(() => Date.now())

  useEffect(() => {
    let cancelled = false
    const sync = async () => {
      const started = Date.now()
      try {
        // Anonymous:
        const res = await request<{ now: string }>('/market/clock', { anonymous: true })
        if (cancelled) return
        const roundTrip = Date.now() - started
        const server = new Date(res.now).getTime() + roundTrip / 2
        skew.current = server - Date.now()
      } catch {
        // A clock we cannot sync is the device clock. Worse, but not fatal.
      }
    }
    void sync()
    const resync = window.setInterval(sync, 5 * 60 * 1000)
    return () => {
      cancelled = true
      window.clearInterval(resync)
    }
  }, [])

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const value = useMemo<MarketClock>(() => {
    const now = new Date(tick + skew.current)
    const state = sessionState(now)
    return {
      now,
      open: state.open,
      closesAt: state.closesAt,
      opensAt: state.opensAt,
      frozen: !isSessionOpen(now),
      remaining: (expiresAt) => {
        const target = expiresAt instanceof Date ? expiresAt : new Date(expiresAt)
        return sessionSecondsRemainingSafe(target, now)
      },
      elapsedSince: (from) => {
        const start = from instanceof Date ? from : new Date(from)
        if (Number.isNaN(start.getTime())) return 0
        return sessionSecondsBetween(start, now)
      },
    }
  }, [tick])

  return <MarketClockContext.Provider value={value}>{children}</MarketClockContext.Provider>
}

export function useMarketClock(): MarketClock {
  const ctx = useContext(MarketClockContext)
  if (!ctx) throw new Error('useMarketClock must be used inside MarketClockProvider')
  return ctx
}

/** One implementation of the freeze rule, shared with the API. */
function sessionSecondsRemainingSafe(expiresAt: Date, now: Date): number {
  if (Number.isNaN(expiresAt.getTime())) return 0
  return Math.max(0, sessionSecondsRemaining(expiresAt, now))
}
